import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { Link } from "react-router";
import { config } from "../../../../config";
import { useSupabaseCallback } from "../../../../server/supabase/hooks";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useAccount } from "../../../generic/hooks/use-account";
import { SupabaseMomentService } from "../../../moment/server/supabase-moment-service";
import { CopyDialog } from "./moment-card-copy-dialog";

import type { MomentCardDropData } from "../../types/moment-card-drop-data";
import type { FC, ReactNode } from "react";

const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
  event.preventDefault();
};

export const MomentCardDropArea: FC<{ children: ReactNode }> = ({ children }) => {
  const dialog = useDialog<
    ({ status: "ready" } & MomentCardDropData) | { status: "loading" | "error" }
  >();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const pipeRef = useRef<HTMLIFrameElement>(null);
  const { data: destinationAccount } = useAccount();

  const messageCallback = useCallback(
    (e: MessageEvent<{ type: "moment_exported" } & { data?: MomentCardDropData }>) => {
      if (
        import.meta.env?.VITE_SERVICE_ENVIRONMENT !== "local" &&
        !e.origin.endsWith("changeengine.com")
      ) {
        return;
      }
      if (e.data.type === "moment_exported" && dialog.open) {
        dialog.handleOpen(e.data.data ? { ...e.data.data, status: "ready" } : { status: "error" });
      }
    },
    [dialog],
  );

  useEffect(() => {
    if (!iframeLoaded) return undefined;
    window.addEventListener("message", messageCallback);
    return () => window.removeEventListener("message", messageCallback);
  }, [iframeLoaded, messageCallback]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const dragId = event.dataTransfer.getData("moment_card_copy");
    pipeRef.current?.contentWindow?.postMessage(
      { type: "moment_drop", dragId },
      { targetOrigin: config.auth0.base_url },
    );
    dialog.handleOpen({ status: "loading" });
  };

  const handleConfirm = useSupabaseCallback(
    async ({ supabase }, data?: { name: string }) => {
      if (dialog.data?.status !== "ready" || !dialog.data) return;

      if (data) {
        // oxlint-disable-next-line react/immutability -- Mutates a hook-returned value; needs a real fix, tracked separately
        dialog.data.moment.title = data.name;
      }

      const { data: momentId, error } = await new SupabaseMomentService(supabase).importMoment(
        dialog.data.moment,
        dialog.data.moment?.id,
      );

      if (error) toast.error("Failed to copy Communication");
      if (momentId)
        toast.success(
          <span>
            {"Communication "}
            <Link to={`/moments/${momentId as string}`}>{dialog.data.moment.title}</Link>
            {" copied successfully."}
          </span>,
          { duration: 5000 },
        );
    },
    [dialog],
  );

  return (
    <>
      <div id="drop-area" onDragOver={handleDragOver} onDrop={handleDrop}>
        {children}
      </div>
      {dialog.data && destinationAccount && (
        <CopyDialog
          open={dialog.open}
          handleClose={dialog.handleClose}
          momentExport={dialog.data}
          destinationAccount={destinationAccount}
          onConfirm={handleConfirm}
        />
      )}
      {/* oxlint-disable-next-line react/iframe-missing-sandbox -- First-party same-origin postMessage bridge; a sandbox would break it */}
      <iframe
        title="pipe"
        ref={pipeRef}
        src={`${config.auth0.base_url}/canbus/`}
        height="0"
        style={{ visibility: "hidden" }}
        onLoad={() => setIframeLoaded(true)}
      />
    </>
  );
};
